import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
// import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
// import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import cpoTransactionApi from '../../utils/api/cpo-transaction';
import TabToggle from '../tab-toggle/TabToggle';

interface CpoCdrSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Formats total time in hours into a short duration string (e.g. 1m 22s).
 * @param {number | string | undefined | null} totalTime - Total time in hours.
 * @returns {string} Formatted duration string.
 */
const formatDuration = (totalTime: number | string | undefined | null): string => {
  if (totalTime == null || Number.isNaN(Number(totalTime))) {
    return '—';
  }
  const totalSeconds = Math.round(Number(totalTime) * 3600);
  if (totalSeconds <= 0) {
    return '00h 00m 00s';
  }
  // Calculate total hours directly (including days)
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  // Pad each value with a leading zero if it is single-digit
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${hh}h ${mm}m ${ss}s`;
};

/**
 * Formats cost amount with currency symbol or code.
 * @param {number | string | undefined | null} amount - Cost amount.
 * @param {string} [currency] - Currency string.
 * @returns {string} Formatted currency text.
 */
const formatCurrency = (amount: number | string | undefined | null, currency?: string): string => {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '—';
  }
  const numericValue = Number(amount);
  const formattedValue = ToFixedValue(numericValue, 2);
  if (currency === 'INR' || !currency) {
    return `₹${formattedValue}`;
  }
  return `${formattedValue} ${currency}`;
};

/**
 * Formats date to DD/MM/YYYY, HH:MM AM/PM string format.
 * @param {string | undefined | null} dateString - Date string in UTC.
 * @returns {string} Formatted date time string.
 */
const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const dateValue = ConvertUTCtoDate(dateString);
    const timeValue = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${dateValue}, ${timeValue}`;
  } catch {
    return String(dateString);
  }
};

/**
 * CpoCdrSidePanel component for displaying Charge Detail Records with a 2-tab drawer layout.
 * @param {CpoCdrSidePanelProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered side panel element.
 */
function CpoCdrSidePanel(properties: CpoCdrSidePanelProperties): React.JSX.Element {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [cdrData, setCdrData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'details' | 'transaction'>('details');

  /**
   * Fetch CPO CDR details by ID
   * @param {string} transactionId - Transaction ID
   */
  const handleFetchCpoTransactionDetails = (transactionId: string) => {
    cpoTransactionApi
      .fetchCpoTransactionById(transactionId)
      .then((response) => {
        setCdrData(response?.data?.data || response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  useEffect(() => {
    let transactionObjectId: string | null = null;
    if (fetchIdFrom === 'queryParams') {
      transactionObjectId =
        searchParameters.get('transactionObjectId') ||
        searchParameters.get('cdrObjectId') ||
        searchParameters.get('cdrId') ||
        searchParameters.get('id') ||
        searchParameters.get('sessionId');
    } else if (fetchIdFrom === 'props') {
      transactionObjectId = id || null;
    }

    if (!transactionObjectId) {
      return;
    }

    setActiveTab('details');
    handleFetchCpoTransactionDetails(transactionObjectId);
  }, [searchParameters, id]);

  const totalCostValue =
    cdrData?.totalCost?.inclVat ??
    cdrData?.totalCost?.exclVat ??
    cdrData?.totalCost ??
    cdrData?.amount;
  const energyValue = cdrData?.totalEnergy ?? cdrData?.powerConsumedInKwh ?? 0;
  const currencyCode = cdrData?.currency || 'INR';

  return (
    <div className="cpo-cdr-side-panel-wrap">
      <div className="panel-scrollable-content">
        {/* Teal Header Summary Card */}
        <div className="cpo-summary-card">
          <div className="cpo-summary-top-row">
            <span className="final-amount-label">{t('finalAmount')}</span>
            <span className="final-amount-value">
              {formatCurrency(totalCostValue, currencyCode)}
            </span>
          </div>
          <div className="cpo-summary-divider" />
          <div className="cpo-summary-bottom-row">
            <div className="summary-metric-item">
              <span className="metric-label">{t('energy')}</span>
              <span className="metric-val">{`${ToFixedValue(energyValue, 3)} kWh`}</span>
            </div>
            <div className="summary-metric-item">
              <span className="metric-label">{t('duration')}</span>
              <span className="metric-val">{formatDuration(cdrData?.totalTime)}</span>
            </div>
            <div className="summary-metric-item">
              <span className="metric-label">{t('totalCost')}</span>
              <span className="metric-val">{formatCurrency(totalCostValue, currencyCode)}</span>
            </div>
          </div>
        </div>

        {/* 2 Top Tab Toggle Navigation */}
        <TabToggle
          tabs={[
            {
              label: t('detailsTransaction'),
              value: 'details',
            },
            {
              label: t('chargingDetails'),
              value: 'transaction',
            },
          ]}
          activeTab={activeTab}
          defaultTab="details"
          isLabelValue
          onChange={(value) => setActiveTab(value as 'details' | 'transaction')}
        />

        {/* Tab 1: Details Transaction */}
        {activeTab === 'details' && (
          <>
            {/* CDR Information Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge cdr-information">
                  <svg viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <h3>{t('cdrInformation')}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('cdrId')}</span>
                    <span className="item-val">{cdrData?.cdrId || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('ocpiId')}</span>
                    <span className="item-val">{cdrData?.ocpiId || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('sessionId')}</span>
                    <span className="item-val">{cdrData?.sessionId || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('transactionId')}</span>
                    <span className="item-val">{cdrData?.transactionId || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('authentication')}</span>
                    <span className="item-val">{cdrData?.authMethod || '—'}</span>
                  </div>
                </div>

                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('currency')}</span>
                    <span className="item-val">{cdrData?.currency || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('startTime')}</span>
                    <span className="item-val">{formatDateTime(cdrData?.startDateTime)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('endTime')}</span>
                    <span className="item-val">{formatDateTime(cdrData?.endDateTime)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('createdAt')}</span>
                    <span className="item-val">{formatDateTime(cdrData?.createdAt)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('lastUpdated')}</span>
                    <span className="item-val">
                      {formatDateTime(cdrData?.lastUpdated || cdrData?.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Token Information Card */}
            {cdrData?.cdrToken && (
              <div className="cpo-section-card">
                <div className="cpo-card-header">
                  <div className="header-icon-badge token">
                    <svg viewBox="0 0 24 24">
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                  </div>
                  <h3>{t('tokenInformation')}</h3>
                </div>
                <div className="cpo-card-grid">
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('tokenUid')}</span>
                      <span className="item-val">{cdrData?.cdrToken?.uid || '—'}</span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('tokenType')}</span>
                      <span className="item-val">{cdrData?.cdrToken?.type || '—'}</span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('contractId')}</span>
                      <span className="item-val">{cdrData?.cdrToken?.contractId || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Location Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge location">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3>{t('location')}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('locationId')}</span>
                    <span className="item-val">{cdrData?.cdrLocation?.id || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('chargingPoint')}</span>
                    <span className="item-val">{cdrData?.chargingPointSn || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('accountId')}</span>
                    <span className="item-val">
                      {cdrData?.accountCustomId || cdrData?.userCustomId || '—'}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('evseUid')}</span>
                    <span className="item-val">{cdrData?.cdrLocation?.evseUid || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('connectorId')}</span>
                    <span className="item-val">{cdrData?.cdrLocation?.connectorId ?? '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tab 2: Transaction Details */}
        {activeTab === 'transaction' && (
          <>
            {/* Energy & Cost Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge energy-cost">
                  <svg viewBox="0 0 24 24">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                </div>
                <h3>{t('energyCost')}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('energyUsed')}</span>
                    <span className="item-val">{`${ToFixedValue(energyValue, 3)} kWh`}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('powerConsumed')}</span>
                    <span className="item-val">{`${ToFixedValue(cdrData?.chargingTransactionRevenue?.powerConsumedInKwh ?? energyValue, 3)} kWh`}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('duration')}</span>
                    <span className="item-val">{formatDuration(cdrData?.totalTime)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('meterStart')}</span>
                    <span className="item-val">
                      {cdrData?.meterStart != null ? ToFixedValue(cdrData.meterStart, 3) : '0.000'}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('meterStop')}</span>
                    <span className="item-val">
                      {cdrData?.meterStop != null
                        ? `${ToFixedValue(cdrData.meterStop, 3)}`
                        : '0.000'}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('costExclVat')}</span>
                    <span className="item-val">
                      {formatCurrency(cdrData?.totalCost?.exclVat, currencyCode)}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('costInclVat')}</span>
                    <span className="item-val">
                      {formatCurrency(cdrData?.totalCost?.inclVat, currencyCode)}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('totalCost')}</span>
                    <span className="item-val">{formatCurrency(totalCostValue, currencyCode)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tariff Information Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge tariff">
                  <svg viewBox="0 0 24 24">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <h3>{t('tariffInformation')}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('tariffId')}</span>
                    <span className="item-val">
                      {cdrData?.tariffs?.[0]?.tariffId || cdrData?.tariffs?.[0]?._id || '—'}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('tariffName')}</span>
                    <span className="item-val">{cdrData?.tariffs?.[0]?.name || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('currentType')}</span>
                    <span className="item-val">{cdrData?.tariffs?.[0]?.currentType || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('tariffType')}</span>
                    <span className="item-val">{cdrData?.tariffs?.[0]?.type || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('chargingPointSn')}</span>
                    <span className="item-val">{cdrData?.chargingPointSn || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('transactionEndReason')}</span>
                    <span className="item-val">{cdrData?.transactionEndReason || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Information Card */}
            {cdrData?.chargingTransactionRevenue && (
              <div className="cpo-section-card">
                <div className="cpo-card-header">
                  <div className="header-icon-badge revenue">
                    <svg viewBox="0 0 24 24">
                      <line x1="12" y1="1" x2="12" y2="23" />
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <h3>{t('revenueInformation')}</h3>
                </div>
                <div className="cpo-card-grid">
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('totalAmount')}</span>
                      <span className="item-val">
                        {formatCurrency(
                          cdrData?.chargingTransactionRevenue?.totalAmount,
                          currencyCode,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('sharableRevenue')}</span>
                      <span className="item-val">
                        {formatCurrency(
                          cdrData?.chargingTransactionRevenue?.sharableRevenue,
                          currencyCode,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('partnerRevenuePercentage')}</span>
                      <span className="item-val">
                        {cdrData?.chargingTransactionRevenue?.partnerRevenuePercentage != null
                          ? `${ToFixedValue(cdrData.chargingTransactionRevenue.partnerRevenuePercentage, 2)}%`
                          : '—'}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('cpoRevenuePercentage')}</span>
                      <span className="item-val">
                        {cdrData?.chargingTransactionRevenue?.cpoRevenuePercentage != null
                          ? `${ToFixedValue(cdrData.chargingTransactionRevenue.cpoRevenuePercentage, 2)}%`
                          : '—'}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('partnerRevenue')}</span>
                      <span className="item-val">
                        {formatCurrency(
                          cdrData?.chargingTransactionRevenue?.partnerRevenue,
                          currencyCode,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('cpoRevenue')}</span>
                      <span className="item-val">
                        {formatCurrency(
                          cdrData?.chargingTransactionRevenue?.cpoRevenue,
                          currencyCode,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('utilityElectricityCost')}</span>
                      <span className="item-val">
                        {formatCurrency(
                          cdrData?.chargingTransactionRevenue?.electricityCostByUtilityProvider,
                          currencyCode,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('energyCost')}</span>
                      <span className="item-val">
                        {formatCurrency(
                          cdrData?.chargingTransactionRevenue?.energyCost,
                          currencyCode,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="cpo-item-cell">
                    <div className="item-content">
                      <span className="item-lbl">{t('stationId')}</span>
                      <span className="item-val">
                        {cdrData?.chargingTransactionRevenue?.chargingStationId || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bottom Disclaimer Banner */}
        <div className="cpo-info-banner">
          <div className="info-banner-icon">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <span>{t('disclaimerText')}</span>
        </div>
      </div>
    </div>
  );
}

export default CpoCdrSidePanel;
